import React, { useState, useEffect } from "react";
import { Book, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllStudentCourses } from "../../../services/course.service";
import { Skeleton, CourseGridSkeleton } from "./ui/Skeleton";
import CourseCard from "./ui/CourseCard";
import SectionHeader from "./ui/SectionHeader";

const Courseware = ({ onCourseClick }) => {
  const [activeSemNumber, setActiveSemNumber] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [semesterTabs, setSemesterTabs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getAllStudentCourses();
        const semNumbersWithCourses = [
          ...new Set(
            data.courses
              .map((course) => course.semNumber || course.semester?.semNumber)
              .filter((semNum) => semNum != null)
          ),
        ].sort((a, b) => a - b);

        const maxSemNumber =
          semNumbersWithCourses.length > 0
            ? Math.max(...semNumbersWithCourses, 8)
            : 8;

        const tabs = [];
        for (let i = 1; i <= maxSemNumber; i++) {
          const hasCourses = semNumbersWithCourses.includes(i);
          tabs.push({
            semNumber: i,
            name: `Semester ${i}`,
            accessible: hasCourses,
          });
        }

        setSemesterTabs(tabs);
        setAllCourses(data.courses || []);
        setUserInfo(data.user);

        if (semNumbersWithCourses.length > 0) {
          setActiveSemNumber(semNumbersWithCourses[0]);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    if (activeSemNumber !== null && semesterTabs.length > 0) {
      const filteredCourses = allCourses.filter((course) => {
        const courseSemNumber = course.semNumber || course.semester?.semNumber;
        return courseSemNumber === activeSemNumber;
      });
      setCourses(filteredCourses);
    } else {
      setCourses([]);
    }
  }, [activeSemNumber, allCourses, semesterTabs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElement = document.querySelector(".semester-dropdown");
      if (isDropdownOpen && dropdownElement && !dropdownElement.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const activeSemesterName =
    semesterTabs.find((s) => s.semNumber === activeSemNumber)?.name || "Select Semester";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto">
        {/* Editorial Header */}
        <div className="bg-white dark:bg-gray-900 px-4 sm:px-6 md:px-8 pt-16 sm:pt-6 md:pt-8 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              {loading ? (
                <>
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-10 sm:h-12 w-56 sm:w-72" />
                  <Skeleton className="h-1 w-12 mt-3" />
                </>
              ) : (
                <>
                  <p className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                    Welcome back,
                  </p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white tracking-tight">
                    {userInfo?.name || "Student"}
                  </h1>
                  <div className="h-1 w-12 bg-accent1 rounded-sm mt-3" />
                </>
              )}
            </div>
            <div className="text-right">
              {loading ? (
                <>
                  <Skeleton className="h-10 sm:h-12 w-12 ml-auto" />
                  <Skeleton className="h-3 w-24 mt-2 ml-auto" />
                </>
              ) : (
                <>
                  <div className="text-stat-sm sm:text-stat font-black text-accent1 tracking-tight">
                    {userInfo?.totalCourses || 0}
                  </div>
                  <p className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Total Courses
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Semester Tabs — Underline style */}
        <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 md:px-8">
          {/* Mobile Dropdown */}
          <div className="md:hidden mb-3">
            <div className="relative semester-dropdown">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-100 dark:bg-gray-800 rounded-sm text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                <span>{activeSemesterName}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 rounded-sm shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
                  {semesterTabs.map((semester) => (
                    <button
                      key={semester.semNumber}
                      onClick={() => {
                        if (semester.accessible) {
                          setActiveSemNumber(semester.semNumber);
                          setIsDropdownOpen(false);
                        }
                      }}
                      disabled={!semester.accessible}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        activeSemNumber === semester.semNumber
                          ? "bg-accent1 text-white"
                          : semester.accessible
                          ? "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          : "text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60"
                      }`}
                    >
                      {semester.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Underline Tabs */}
          <div className="hidden md:flex space-x-1 overflow-x-auto relative">
            {semesterTabs.map((semester) => (
              <button
                key={semester.semNumber}
                onClick={() => {
                  if (semester.accessible) {
                    setActiveSemNumber(semester.semNumber);
                  }
                }}
                disabled={!semester.accessible}
                className={`relative px-4 lg:px-5 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeSemNumber === semester.semNumber
                    ? "text-accent1"
                    : semester.accessible
                    ? "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                }`}
                aria-label={`${semester.name}${!semester.accessible ? " (No courses available)" : ""}`}
              >
                {semester.name}
                {activeSemNumber === semester.semNumber && (
                  <motion.div
                    layoutId="semester-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent1"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Course Grid */}
        <div className="p-4 sm:p-6 md:p-8">
          {loading ? (
            <CourseGridSkeleton />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSemNumber}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
              >
                <SectionHeader
                  title={
                    activeSemNumber !== null
                      ? `Semester ${activeSemNumber} Courses`
                      : "Courses"
                  }
                  subtitle={`${courses.length} ${courses.length === 1 ? "course" : "courses"} available`}
                />

                {courses.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm p-16 text-center">
                    <Book className="h-14 w-14 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-1">
                      No courses available
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      Check back later for new courses in this semester.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                    {courses.map((course, index) => (
                      <CourseCard
                        key={course._id}
                        course={course}
                        index={index}
                        showAssignmentCount={false}
                        showSemesterWeeks={true}
                        onCourseClick={onCourseClick}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
};

export default Courseware;
